import XLSX from 'xlsx'
import Papa from 'papaparse'
const _snakeCase = require('lodash/snakeCase')

export const toDataURL = (url: any) =>
  fetch(url)
    .then((response) => response.blob())
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        }),
    )
    .catch((e) => {
      console.log(e)
    })

const dataURLtoFile = (dataurl: any, filename: any) => {
  const arr = dataurl.split(',')
  const mime = arr[0].match(/:(.*?);/)[1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, {
    type: mime,
  })
}

export const importContentCsv = (file: any) => {
  return new Promise((resolve, reject) => {
    const v2Headers = [
      'topic',
      'quiz',
      'question_type',
      'question',
      'choice_1',
      'choice_2',
      'choice_3',
      'choice_4',
      'correct_choices',
      'general_feedback',
      'choice_1_feedback',
      'choice_2_feedback',
      'choice_3_feedback',
      'choice_4_feedback',
      'shuffle_answers',
      'reinforce',
      'external_id',
    ]

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => _snakeCase(header),
      complete: (results) => {
        try {
          if (results.errors.length > 0) {
            reject('An error occurred while trying to read this template')
            return
          }

          if (results.data.length === 0) {
            reject('File appears to be empty')
            return
          }

          const headersFound = results.meta.fields || []
          const checkHeaders = (arr, target) => target.every((val: string) => arr.includes(val))

          if (!checkHeaders(headersFound, v2Headers)) {
            reject('Template is missing one or more required headers')
            return
          }

          // Skip the first data row (instructions row), matching XLSX template behavior
          const data = (results.data as any[]).slice(1)

          if (data.length === 0) {
            reject('File appears to be empty')
            return
          }

          resolve(data)
        } catch (e) {
          reject('An error occurred while trying to read this template')
        }
      },
      error: () => {
        reject('An error occurred while trying to read this template')
      },
    })
  })
}

export const importContent = (file: any) => {
  // Detect CSV files and route to CSV parser
  const isCsv = file.type === 'text/csv' || file.name?.toLowerCase().endsWith('.csv')
  if (isCsv) {
    return importContentCsv(file)
  }

  // Continue with XLSX parsing for Excel files
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onabort = () => reject()
    reader.onerror = () => reject()
    reader.onloadend = () => {
      try {
        // Detect if file is CSV based on MIME type or extension
        const workbook = XLSX.read(reader.result, { type: 'array' })

        let validSheet
        let header = [] as any
        const v1Headers = [
          'topic',
          'quiz',
          'question_type',
          'question',
          'answer_1',
          'answer_2',
          'answer_3',
          'answer_4',
          'about_the_answer',
          'key',
          'external_id',
        ]
        const v2Headers = [
          'topic',
          'quiz',
          'question_type',
          'question',
          'choice_1',
          'choice_2',
          'choice_3',
          'choice_4',
          'correct_choices',
          'general_feedback',
          'choice_1_feedback',
          'choice_2_feedback',
          'choice_3_feedback',
          'choice_4_feedback',
          'shuffle_answers',
          'reinforce',
          'external_id',
        ]

        Object.values(workbook.Sheets).map((sheet) => {
          const headersFound = [] as any
          // @ts-ignore
          const range = XLSX.utils.decode_range(sheet['!ref'])
          let C
          const R = range.s.r
          for (C = range.s.c; C <= range.e.c; ++C) {
            /* walk every column in the range */
            const cell = sheet[XLSX.utils.encode_cell({ c: C, r: R })]
            /* find the cell in the first row */
            let hdr = false as any
            if (cell && cell.t) {
              hdr = XLSX.utils.format_cell(cell)
            }
            if (hdr) {
              headersFound.push(_snakeCase(hdr))
            }
          }

          const checkHeaders = (arr, target) => target.every((val: string) => arr.includes(val))

          if (checkHeaders(headersFound, v2Headers)) {
            validSheet = sheet
            header = headersFound
          } else if (checkHeaders(headersFound, v1Headers)) {
            reject(
              "The template you're using is out of date. Please download the new template, update your copy, and try again.",
            )
          }
        })

        if (!validSheet) {
          reject('Template is missing one or more required headers')
        }

        const parsed = XLSX.utils.sheet_to_json(validSheet, {
          raw: isCsv ? true : false,
          header,
          range: isCsv ? 1 : 2,
        })

        if (parsed?.length === 0) {
          reject('File appears to be empty')
        }

        resolve(parsed)
      } catch (e) {
        reject('An error occurred while trying to read this template')
      }
    }

    reader.readAsArrayBuffer(file)
  })
}

export const createFileFromPath = async (FileUrl: any, name = 'default.jpg') => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise<File>(async (resolve) => {
    const dataUrl = await toDataURL(FileUrl)
    const fileData = dataURLtoFile(dataUrl, name)
    resolve(fileData)
  })
}

export const createFileFromBlob = async (blobUrl: string, fileName = 'default') => {
  const blobResponse = await (await fetch(blobUrl)).blob()
  const file = new File([blobResponse], `${fileName}.${blobResponse?.type?.split('/')[1]}`, {
    type: blobResponse.type,
  })
  return file
}

export const SUPPORTED_FILES = [
  'aac',
  'ai',
  'aif',
  'aifc',
  'aiff',
  'au',
  'avi',
  'bmp',
  'csv',
  'diff',
  'doc',
  'docx',
  'flac',
  'gif',
  'heic',
  'jpeg',
  'jpg',
  'm2v',
  'm4a',
  'm4v',
  'md',
  'mkv',
  'mov',
  'mp2',
  'mp3',
  'mp4',
  'mpeg',
  'mpg',
  'mpv',
  'odp',
  'ods',
  'odt',
  'ogg',
  'pages',
  'pdf',
  'png',
  'ppt',
  'pptx',
  'psd',
  'qt',
  'rtf',
  'svg',
  'tga',
  'tif',
  'tiff',
  'txt',
  'wma',
  'wmv',
  'wpd',
  'xls',
  'xlsb',
  'xlsm',
  'xlsx',
  'xml',
  'xsd',
  'xsl',
  'webp',
]

export const isFileSupported = (filename: string | undefined) => {
  if (!filename) return false
  const splittedName = filename.split('.')
  return SUPPORTED_FILES.includes(splittedName[splittedName.length - 1].toLocaleLowerCase())
}

export const isValidURL = (string) => {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i',
  ) // fragment locator
  return !!pattern.test(string)
}

export const excelTypes = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv', // technically not excel, but I think it makes sense here
  'application/vnd.google-apps.spreadsheet',
  'xls',
  'xlsm',
  'xlsx',
  'xsl',
]

export const imageTypes = [
  'image/bmp',
  'image/gif',
  'image/vnd.microsoft.icon',
  'image/jpeg',
  'image/png',
  'image/svg+xml',
  'image/tiff',
  'image/webp',
  'ai',
  'bmp',
  'gif',
  'eps',
  'heic',
  'jpeg',
  'jpg',
  'png',
  'ps',
  'psd',
  'svg',
  'tif',
  'tiff',
  'dcm',
  'dicm',
  'dicom',
  'svs',
  'tga',
]

export const mediaTypes = [
  'video/x-msvideo',
  'mp4',
  'video/mp4',
  'video/mpeg',
  'video/ogg',
  'video/mp2t',
  'video/webm',
  'video/3gpp',
  'video/3gpp2',
  'audio/aac',
  'application/x-cdf',
  'video/mp4',
]

export const pdfTypes = ['application/pdf', 'pdf']

export const slideTypes = [
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.google-apps.presentation',
  'ppt',
  'pptx',
]

export const wordTypes = [
  'application/msword',
  'application/vndoasisopendocumenttext',
  'application/vndopenxmlformats-officedocumentwordprocessingmldocument',
  'doc',
  'docx',
  'gdoc',
  'gsheet',
  'ods',
  'odt',
  'pages',
  'wpd',
]

export const soundTypes = [
  'audio/aac',
  'audio/ac3',
  'audio/eac3',
  'audio/flac',
  'audio/mp3',
  'audio/mp4',
  'audio/mpeg',
  'audio/mpeg4-generic',
  'audio/mpga',
  'audio/ogg',
  'audio/opus',
  'audio/pcma',
  'audio/webm',
  'audio/x-ms-wma',
  'mp3',
]

export const GetBackgroundColorOfMimeType = (fileType) => {
  const validUrl = fileType === 'url' ? true : isValidURL(fileType)
  const massagedExtension = String(fileType).toLocaleLowerCase().replace('.', '')

  if (mediaTypes.includes(massagedExtension)) return '#F2C94C'
  else if (slideTypes.includes(massagedExtension)) return 'rgba(255, 115, 36, 0.80)'
  else if (imageTypes.includes(massagedExtension)) return '#F098FF'
  else if (excelTypes.includes(massagedExtension)) return '#1FE08F'
  else if (pdfTypes.includes(massagedExtension)) return '#ED7166'
  else if (wordTypes.includes(massagedExtension)) return '#6E94F7'
  else if (soundTypes.includes(massagedExtension)) return 'rgba(255, 62, 19, 0.70)'
  else if (validUrl) return 'rgba(85, 27, 250, 0.7)'
  else return '#1FE08F'
}
